import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, Role, Capability } from '../types/auth';
import { api } from '../utils/api';

interface CreateUserData {
  email: string;
  username: string;
  /** Omit for single sign-on users: they get no usable password. */
  password?: string;
  role_ids: number[];
  capability_ids: number[];
  is_staff: boolean;
  auth_source?: 'password' | 'sso';
}

interface UpdateUserData {
  email?: string;
  username?: string;
  phone_number?: string;
  role_ids?: number[];
  capability_ids?: number[];
  is_staff?: boolean;
  is_active?: boolean;
}

interface CreateRoleData {
  name: string;
  capability_ids: number[];
}

interface UpdateRoleData {
  name?: string;
  capability_ids?: number[];
}

export interface APIClient {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  secret_hint: string;
  created_at: string;
  updated_at: string;
}

export interface APIClientCreateResponse extends APIClient {
  api_key: string;
}

export interface RotateKeyResponse {
  api_key: string;
  secret_hint: string;
}

export interface CreateAPIClientData {
  name: string;
  description: string;
}

// Tenant branding (company logo shown in the top bar)
export type LogoBackdrop = 'none' | 'square' | 'circle';

export interface TenantBranding {
  logo_url: string | null;
  backdrop: LogoBackdrop;
  scale: number;
}

export const useTenantLogo = () => {
  return useQuery({
    queryKey: ['tenant-logo'],
    queryFn: async () => {
      const res = await api.request<{ data: TenantBranding }>('/api/admin/tenant/logo');
      return res.data;
    },
  });
};

export const useUploadTenantLogo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, backdrop, scale }: { file: File; backdrop: LogoBackdrop; scale: number }) => {
      const form = new FormData();
      form.append('logo', file);
      form.append('backdrop', backdrop);
      form.append('scale', String(scale));
      const res = await api.request<{ data: TenantBranding }>('/api/admin/tenant/logo', {
        method: 'POST',
        body: form,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-logo'] });
    },
  });
};

export const useUpdateTenantBranding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: { backdrop?: LogoBackdrop; scale?: number }) => {
      const res = await api.request<{ data: TenantBranding }>('/api/admin/tenant/logo', {
        method: 'PATCH',
        body: patch,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-logo'] });
    },
  });
};

export const useRemoveTenantLogo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.request<{ data: TenantBranding }>('/api/admin/tenant/logo', {
        method: 'DELETE',
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-logo'] });
    },
  });
};

// Users
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.request<User[]>('/api/admin/users'),
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (user: CreateUserData) =>
      api.request<User>('/api/admin/users', {
        method: 'POST',
        body: user,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (user: UpdateUserData) =>
      api.request<User>(`/api/admin/users/${userId}`, {
        method: 'PATCH',
        body: user,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/** Deactivate / reactivate any user by id (the row's own hook needs a fixed id). */
export const useSetUserActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isActive }: { userId: number; isActive: boolean }) =>
      api.request<User>(`/api/admin/users/${userId}`, {
        method: 'PATCH',
        body: { is_active: isActive },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.request<{ message: string }>(`/api/admin/users/${userId}`, {
        method: 'DELETE',
      });
      if (!response) {
        throw new Error('Failed to delete user');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      console.error('Error deleting user:', error);
    },
  });
};

export const useResetUserPassword = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { password: string }) =>
      api.request<User>(`/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useSendTestSms = (userId: number) =>
  useMutation({
    mutationFn: () =>
      api.request<{ message: string }>(`/api/admin/users/${userId}/test-sms`, {
        method: 'POST',
      }),
  });

// Roles
export const useRoles = () => {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api.request<Role[]>('/api/admin/roles'),
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (role: CreateRoleData) =>
      api.request<Role>('/api/admin/roles', {
        method: 'POST',
        body: role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

export const useUpdateRole = (roleId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (role: UpdateRoleData) =>
      api.request<Role>(`/api/admin/roles/${roleId}`, {
        method: 'PATCH',
        body: role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) =>
      api.request<void>(`/api/admin/roles/${roleId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

// Capabilities
export const useCapabilities = () => {
  return useQuery({
    queryKey: ['capabilities'],
    queryFn: () => api.request<Capability[]>('/api/admin/capabilities'),
  });
};

export const useAPIClients = () => {
  return useQuery({
    queryKey: ['api-clients'],
    queryFn: () => api.request<APIClient[]>('/api/admin/api-clients'),
  });
};

export const useCreateAPIClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAPIClientData) =>
        api.request<APIClientCreateResponse>('/api/admin/api-clients/', {
          method: 'POST',
          body: data,
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] });
    },
  });
};

export const useDeactivateAPIClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
        api.request<APIClient>(`/api/admin/api-clients/${id}/deactivate`, {
          method: 'PATCH',
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] });
    },
  });
};

export const useActivateAPIClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
        api.request<APIClient>(`/api/admin/api-clients/${id}/activate`, {
          method: 'PATCH',
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] });
    },
  });
};

export const useRotateAPIClientKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
        api.request<RotateKeyResponse>(`/api/admin/api-clients/${id}/rotate-key`, {
          method: 'POST',
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] });
    },
  });
};

export const useDeleteAPIClient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
        api.request<void>(`/api/admin/api-clients/${id}`, {
          method: 'DELETE',
        }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] });
    },
  });
};

// ---------------------------------------------------------------------------
// Per-user notification subscriptions (admin managing another user's alerts)

export type NotificationChannel = 'email' | 'sms';

export interface AdminSubscriptionEntry {
  channels: Record<NotificationChannel, boolean>;
  snoozed_until: string | null;
}

export interface AdminSubscriptionProject {
  id: number;
  name: string;
  /** The project's alert radius, shared by every subscriber. */
  radius_km: number;
  subscriptions: Record<string, AdminSubscriptionEntry>;
}

export interface AdminUserSubscriptions {
  user: {
    id: number;
    username: string;
    email: string;
    phone_number: string;
  };
  kinds: string[];
  channels: NotificationChannel[];
  available_kinds: Record<string, boolean>;
  configured_kinds: Record<string, boolean>;
  max_radius_km: number;
  projects: AdminSubscriptionProject[];
}

const adminSubscriptionsKey = (userId: number) => [
  'admin-user-subscriptions',
  userId,
];

export const useAdminUserSubscriptions = (userId: number) =>
  useQuery({
    queryKey: adminSubscriptionsKey(userId),
    queryFn: async () => {
      const res = await api.request<{ data: AdminUserSubscriptions }>(
        `/api/admin/users/${userId}/notification-subscriptions`
      );
      return res.data;
    },
    enabled: Number.isFinite(userId),
  });

export interface AdminSubscriptionUpdate {
  project_id: number;
  subscriptions: Record<
    string,
    { channels?: Partial<Record<NotificationChannel, boolean>> }
  >;
}

export const useUpdateAdminUserSubscriptions = (userId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: AdminSubscriptionUpdate) =>
      api.request<{ data: { project: AdminSubscriptionProject } }>(
        `/api/admin/users/${userId}/notification-subscriptions`,
        { method: 'PATCH', body: update }
      ),
    onSuccess: (result) => {
      // Merge the refreshed project entry in place rather than refetching the
      // whole table, so rapid toggling across rows doesn't thrash.
      queryClient.setQueryData<AdminUserSubscriptions | undefined>(
        adminSubscriptionsKey(userId),
        (prev) =>
          prev && {
            ...prev,
            projects: prev.projects.map((p) =>
              p.id === result.data.project.id ? result.data.project : p
            ),
          }
      );
    },
  });
};

/**
 * The same whole-tenant subscription table, for the caller's own alerts
 * (My Account). Shares the admin payload shape deliberately — the backend
 * builds both from one helper, so one table component renders either.
 */
const mySubscriptionsKey = ['my-subscriptions'];
const MY_SUBSCRIPTIONS_URL = '/api/me/notification-subscriptions';

export const useMySubscriptions = () =>
  useQuery({
    queryKey: mySubscriptionsKey,
    queryFn: async () => {
      const res = await api.request<{ data: AdminUserSubscriptions }>(
        MY_SUBSCRIPTIONS_URL
      );
      return res.data;
    },
  });

export const useUpdateMySubscriptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: AdminSubscriptionUpdate) =>
      api.request<{ data: { project: AdminSubscriptionProject } }>(
        MY_SUBSCRIPTIONS_URL,
        { method: 'PATCH', body: update }
      ),
    onSuccess: (result) => {
      // Merge the refreshed project row in place rather than refetching the
      // whole table, so rapid toggling across rows doesn't thrash.
      queryClient.setQueryData<AdminUserSubscriptions | undefined>(
        mySubscriptionsKey,
        (prev) =>
          prev && {
            ...prev,
            projects: prev.projects.map((p) =>
              p.id === result.data.project.id ? result.data.project : p
            ),
          }
      );
      // The project-scoped Notifications tab reads the same rows, under a
      // ['project', id, 'notification-subscriptions'] key — hence a predicate
      // rather than a prefix match.
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes('notification-subscriptions'),
      });
    },
  });
};
